import { useEffect, useRef, useState, useCallback } from 'react';

// In dev, Vite proxies /go2rtc-api → go2rtc server (avoids CORS).
// In production, use absolute URLs to cameras.corporacionasi.com directly.
const GO2RTC_URL = import.meta.env.VITE_GO2RTC_URL as string | undefined;

// API base for go2rtc endpoints.
// Production: absolute URL using VITE_GO2RTC_URL (e.g. https://cameras.corporacionasi.com/go2rtc/api)
// Dev: relative path proxied by Vite (/go2rtc-api)
const GO2RTC_API_BASE = GO2RTC_URL ? `${GO2RTC_URL}/go2rtc/api` : '/go2rtc-api';

// WebSocket base for go2rtc (MSE + talkback signaling).
// Production: wss://cameras.corporacionasi.com/go2rtc/api
// Dev: ws(s)://localhost:3000/go2rtc-api (Vite proxy)
const GO2RTC_WS_BASE = GO2RTC_URL
  ? GO2RTC_URL.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:') + '/go2rtc/api'
  : `${typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${typeof window !== 'undefined' ? window.location.host : 'localhost:3000'}/go2rtc-api`;

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'failed';
type Mode = 'webrtc' | 'mse';

export interface Go2rtcPlayerState {
  state: ConnectionState;
  mode: Mode | null;
  error: string | null;
  bitrateKbps: number | null;
  resolution: string | null;
  retry: () => void;
}

interface Go2rtcPlayerOptions {
  /** Force a specific transport. Default: 'auto' (WebRTC → MSE fallback) */
  preferredMode?: 'webrtc' | 'mse' | 'auto';
}

/**
 * Hook to play a go2rtc stream via WebRTC (primary) with MSE fallback.
 *
 * go2rtc signaling:
 *   POST /api/webrtc?src=<stream_id>  body=SDP offer → SDP answer
 *
 * MSE fallback:
 *   WS /api/ws?src=<stream_id>  send {"type":"mse"} → binary MP4 fragments
 */
export function useGo2rtcPlayer(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  streamId: string | null | undefined,
  options?: Go2rtcPlayerOptions,
): Go2rtcPlayerState {
  const preferredMode = options?.preferredMode ?? 'auto';
  const [state, setState] = useState<ConnectionState>('idle');
  const [mode, setMode] = useState<Mode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bitrateKbps, setBitrateKbps] = useState<number | null>(null);
  const [resolution, setResolution] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const freezeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastTimeRef = useRef(0);
  const frozenSinceRef = useRef(0);
  // MSE byte tracking
  const mseBytesRef = useRef(0);
  const mseLastBytesRef = useRef(0);
  const mseLastTsRef = useRef(0);

  const cleanupStats = useCallback(() => {
    if (statsTimerRef.current) {
      clearInterval(statsTimerRef.current);
      statsTimerRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
    wsRef.current?.close();
    wsRef.current = null;
    if (freezeTimerRef.current) {
      clearInterval(freezeTimerRef.current);
      freezeTimerRef.current = null;
    }
    cleanupStats();
  }, [cleanupStats]);

  // WebRTC stats polling — bitrate + resolution from getStats()
  const startWebRTCStats = useCallback((pc: RTCPeerConnection) => {
    cleanupStats();
    let prevBytes = 0;
    let prevTs = 0;
    statsTimerRef.current = setInterval(async () => {
      try {
        const stats = await pc.getStats();
        stats.forEach((report) => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            const bytes: number = report.bytesReceived ?? 0;
            const ts: number = report.timestamp ?? 0;
            if (prevTs > 0 && ts > prevTs) {
              const dt = (ts - prevTs) / 1000;
              setBitrateKbps(Math.round((bytes - prevBytes) * 8 / dt / 1000));
            }
            prevBytes = bytes;
            prevTs = ts;
            if (report.frameWidth && report.frameHeight) {
              setResolution(`${report.frameWidth}×${report.frameHeight}`);
            }
          }
        });
      } catch { /* peer closed */ }
    }, 2000);
  }, [cleanupStats]);

  // MSE stats polling — bitrate from byte counter
  const startMSEStats = useCallback(() => {
    cleanupStats();
    mseBytesRef.current = 0;
    mseLastBytesRef.current = 0;
    mseLastTsRef.current = Date.now();
    statsTimerRef.current = setInterval(() => {
      const now = Date.now();
      const dt = (now - mseLastTsRef.current) / 1000;
      if (dt > 0) {
        const kbps = Math.round((mseBytesRef.current - mseLastBytesRef.current) * 8 / dt / 1000);
        setBitrateKbps(kbps);
      }
      mseLastBytesRef.current = mseBytesRef.current;
      mseLastTsRef.current = now;
    }, 2000);
  }, [cleanupStats]);

  // Freeze detection: if video.currentTime doesn't advance for 5s while
  // connected, the decoder likely lost a keyframe → auto-reconnect.
  const startFreezeDetection = useCallback((video: HTMLVideoElement) => {
    if (freezeTimerRef.current) clearInterval(freezeTimerRef.current);
    lastTimeRef.current = video.currentTime;
    frozenSinceRef.current = 0;

    freezeTimerRef.current = setInterval(() => {
      const now = video.currentTime;
      if (now === lastTimeRef.current && !video.paused) {
        frozenSinceRef.current += 2000;
        if (frozenSinceRef.current >= 5000) {
          console.warn('[go2rtc] Video frozen for 5s, auto-reconnecting...');
          setAttempt((a) => a + 1);
        }
      } else {
        frozenSinceRef.current = 0;
      }
      lastTimeRef.current = now;
    }, 2000);
  }, []);

  useEffect(() => {
    if (!GO2RTC_URL || !streamId || !videoRef.current) {
      setState('idle');
      return;
    }

    let cancelled = false;
    const video = videoRef.current;

    setState('connecting');
    setError(null);
    setMode(null);
    setBitrateKbps(null);
    setResolution(null);

    const onMseBytes = (n: number) => { mseBytesRef.current += n; };

    // Try WebRTC first, fall back to MSE — or force one if preferred
    (async () => {
      const tryWebRTC = async () => {
        const pc = await connectWebRTC(video, streamId, cancelled, () => cancelled);
        if (cancelled) { pc.close(); return; }
        pcRef.current = pc;
        setState('connected');
        setMode('webrtc');
        startFreezeDetection(video);
        startWebRTCStats(pc);
        pc.onconnectionstatechange = () => {
          if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
            console.warn(`[go2rtc] WebRTC ${pc.connectionState}, auto-reconnecting...`);
            setAttempt((a) => a + 1);
          }
        };
      };

      const tryMSE = async () => {
        await connectMSE(video, streamId, () => cancelled, onMseBytes);
        if (cancelled) return;
        setState('connected');
        setMode('mse');
        startFreezeDetection(video);
        startMSEStats();
        // Resolve resolution from video element once loaded
        const onResize = () => {
          if (video.videoWidth && video.videoHeight) {
            setResolution(`${video.videoWidth}×${video.videoHeight}`);
          }
        };
        video.addEventListener('resize', onResize);
        onResize();
        cleanupRef.current = () => video.removeEventListener('resize', onResize);
      };

      try {
        if (preferredMode === 'mse') {
          await tryMSE();
        } else if (preferredMode === 'webrtc') {
          await tryWebRTC();
        } else {
          // Auto: WebRTC → MSE fallback
          try {
            await tryWebRTC();
          } catch (webrtcErr) {
            if (cancelled) return;
            console.warn('[go2rtc] WebRTC failed, trying MSE:', webrtcErr);
            await tryMSE();
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[go2rtc] Connection failed:', err);
        setState('failed');
        setError(err instanceof Error ? err.message : 'No se pudo conectar');
      }
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [streamId, attempt, cleanup, videoRef, startFreezeDetection, startWebRTCStats, startMSEStats, preferredMode]);

  const retry = useCallback(() => {
    cleanup();
    setState('idle');
    setError(null);
    setMode(null);
    setBitrateKbps(null);
    setResolution(null);
    setAttempt((a) => a + 1);
  }, [cleanup]);

  return { state, mode, error, bitrateKbps, resolution, retry };
}

// ─── WebRTC Connection ────────────────────────────────────────

async function connectWebRTC(
  video: HTMLVideoElement,
  streamId: string,
  cancelled: boolean,
  isCancelled: () => boolean,
): Promise<RTCPeerConnection> {
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  });

  // We want to receive video + audio
  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });

  pc.ontrack = (ev) => {
    if (ev.streams.length > 0) {
      video.srcObject = ev.streams[0];
    }
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  // Wait for ICE gathering to complete (or timeout after 2s)
  if (pc.iceGatheringState !== 'complete') {
    await new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, 2000);
      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === 'complete') {
          clearTimeout(timer);
          resolve();
        }
      };
    });
  }

  if (isCancelled()) {
    pc.close();
    throw new Error('Cancelled');
  }

  // Send offer to go2rtc signaling endpoint
  const res = await fetch(`${GO2RTC_API_BASE}/webrtc?src=${encodeURIComponent(streamId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(pc.localDescription),
  });

  if (!res.ok) {
    pc.close();
    throw new Error(`go2rtc WebRTC error: ${res.status}`);
  }

  const answer = await res.json();

  if (isCancelled()) {
    pc.close();
    throw new Error('Cancelled');
  }

  await pc.setRemoteDescription(answer);

  // Wait for connection
  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      pc.close();
      reject(new Error('WebRTC connection timeout'));
    }, 10000);

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        clearTimeout(timeout);
        resolve();
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        clearTimeout(timeout);
        pc.close();
        reject(new Error('WebRTC connection failed'));
      }
    };
  });

  return pc;
}

// ─── MSE Fallback ─────────────────────────────────────────────

async function connectMSE(
  video: HTMLVideoElement,
  streamId: string,
  isCancelled: () => boolean,
  onBytes?: (bytes: number) => void,
): Promise<void> {
  if (!('MediaSource' in window)) {
    throw new Error('MSE not supported');
  }

  // Use GO2RTC_WS_BASE — absolute URL in production, relative proxy path in dev
  return new Promise<void>((resolve, reject) => {
    const ws = new WebSocket(`${GO2RTC_WS_BASE}/ws?src=${encodeURIComponent(streamId)}`);
    ws.binaryType = 'arraybuffer';

    let ms: MediaSource | null = null;
    let sb: SourceBuffer | null = null;
    let resolved = false;
    const queue: ArrayBuffer[] = [];

    const timeout = setTimeout(() => {
      ws.close();
      reject(new Error('MSE connection timeout'));
    }, 10000);

    ws.onopen = () => {
      if (isCancelled()) { ws.close(); return; }
      ws.send(JSON.stringify({ type: 'mse' }));
    };

    ws.onmessage = (ev) => {
      if (isCancelled()) { ws.close(); return; }

      // First text message contains codec info (e.g. "mse:video/mp4; codecs=\"...\"")
      if (typeof ev.data === 'string') {
        const match = ev.data.match(/^mse:(.+)$/);
        if (!match) return;

        const mimeType = match[1];
        if (!MediaSource.isTypeSupported(mimeType)) {
          ws.close();
          reject(new Error(`Unsupported codec: ${mimeType}`));
          return;
        }

        ms = new MediaSource();
        video.src = URL.createObjectURL(ms);

        ms.addEventListener('sourceopen', () => {
          try {
            sb = ms!.addSourceBuffer(mimeType);
            sb.mode = 'segments';
            sb.addEventListener('updateend', () => {
              if (queue.length > 0 && sb && !sb.updating) {
                sb.appendBuffer(queue.shift()!);
              }
            });
          } catch (err) {
            ws.close();
            reject(err instanceof Error ? err : new Error('SourceBuffer error'));
          }
        });

        return;
      }

      // Binary data — MP4 fragment
      if (ev.data instanceof ArrayBuffer && sb) {
        onBytes?.(ev.data.byteLength);
        if (!resolved) {
          clearTimeout(timeout);
          resolved = true;
          resolve();
        }
        if (sb.updating) {
          queue.push(ev.data);
        } else {
          try {
            sb.appendBuffer(ev.data);
          } catch {
            // Buffer full — drop oldest data
            queue.push(ev.data);
          }
        }
      }
    };

    ws.onerror = () => {
      clearTimeout(timeout);
      if (!resolved) reject(new Error('MSE WebSocket error'));
    };

    ws.onclose = () => {
      clearTimeout(timeout);
      if (!resolved) reject(new Error('MSE WebSocket closed'));
    };
  });
}

// ─── Two-Way Audio (Talk-back) ────────────────────────────────

export interface TalkbackState {
  isActive: boolean;
  isStarting: boolean;
  error: string | null;
  /** Toggle microphone. Pass the main go2rtc stream id (not _sub / _mid). */
  toggle: (streamId: string) => Promise<void>;
  stop: () => void;
}

/**
 * Hook for sending microphone audio to a camera via go2rtc two-way audio.
 *
 * Opens a separate WebRTC connection to go2rtc with a sendonly audio track.
 * go2rtc forwards the incoming audio to the camera via RTSP back-channel
 * (for cameras that support ONVIF / Hikvision audio back-channel).
 *
 * Usage:
 *   const tb = useGo2rtcTalkback();
 *   <button onClick={() => tb.toggle(camera.go2rtc_stream_id!)}>Hablar</button>
 */
export function useGo2rtcTalkback(): TalkbackState {
  const [isActive, setIsActive] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const micRef = useRef<MediaStream | null>(null);

  const stop = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;
    micRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current = null;
    setIsActive(false);
    setError(null);
    setIsStarting(false);
  }, []);

  const toggle = useCallback(async (streamId: string) => {
    if (isActive) { stop(); return; }

    setIsStarting(true);
    setError(null);

    try {
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      micRef.current = mic;

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      pcRef.current = pc;

      // Add mic track; we only send audio (no video)
      mic.getAudioTracks().forEach((track) => pc.addTrack(track, mic));

      // Set all transceivers to sendonly — we are only sending to the camera
      pc.getTransceivers().forEach((t) => {
        if (t.sender.track?.kind === 'audio') t.direction = 'sendonly';
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Wait for ICE candidates (or timeout after 2 s)
      if (pc.iceGatheringState !== 'complete') {
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, 2000);
          pc.onicegatheringstatechange = () => {
            if (pc.iceGatheringState === 'complete') { clearTimeout(timer); resolve(); }
          };
        });
      }

      // POST SDP offer to go2rtc via absolute URL in production, proxy in dev
      const res = await fetch(`${GO2RTC_API_BASE}/webrtc?src=${encodeURIComponent(streamId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pc.localDescription),
      });

      if (!res.ok) throw new Error(`go2rtc error: ${res.status}`);

      const answer = await res.json() as RTCSessionDescriptionInit;
      await pc.setRemoteDescription(answer);

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          stop();
        }
      };

      setIsActive(true);
    } catch (err) {
      stop();
      setError(err instanceof Error ? err.message : 'No se pudo iniciar el audio');
    } finally {
      setIsStarting(false);
    }
  }, [isActive, stop]);

  // Ensure mic is released when component unmounts
  useEffect(() => () => { stop(); }, [stop]);

  return { isActive, isStarting, error, toggle, stop };
}
